// src/components/ClientsPage/ClientsPage.jsx

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../UI/Modal";
import Button from "../UI/Button";
import Card from "../UI/Card";
import ConfirmModal from "../UI/ConfirmModal";
import { authFetch } from "../api/api";
import { useTranslation } from "react-i18next";


const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const API_URL = `${BASE_URL}/api/clients/`;

const emptyForm = {
  name: "",
  commission_rate: "",
  comment: "",
  
};

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingClient, setEditingClient] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const { t } = useTranslation("ClientsPage");

  const navigate = useNavigate();

  /* ================= LOAD ================= */
  const fetchClients = async () => {
    try {
      const res = await authFetch(API_URL);
      const data = await res.json();
      setClients(data);
    } catch (err) {
      console.error("Ошибка загрузки клиентов:", err);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  /* ================= MODAL ================= */
  const openCreateModal = () => {
    setEditingClient(null);
    setForm(emptyForm);
    setIsOpen(true);
  };

  const openEditModal = (client) => {
    setEditingClient(client);
    setForm({
      name: client.name || "",
      commission_rate: client.commission_rate || "",
      comment: client.comment || "",
    });
    setIsOpen(true);
  };

  /* ================= SAVE ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) return;

    const isEdit = !!editingClient;
    const url = isEdit ? `${API_URL}${editingClient.id}/` : API_URL;
    const method = isEdit ? "PUT" : "POST";

    try {
      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (res.ok) {
        setForm(emptyForm);
        setEditingClient(null);
        setIsOpen(false);
        fetchClients();
      }
    } catch (err) {
      console.error("Ошибка сохранения клиента:", err);
    }
  };

  /* ================= DELETE ================= */
  const handleDeleteClient = async () => {
  if (!clientToDelete) return;

  try {
    await authFetch(`${API_URL}${clientToDelete.id}/`, {
      method: "DELETE",
    });

    setClients((prev) =>
      prev.filter((c) => c.id !== clientToDelete.id)
    );

    setShowDeleteConfirm(false);
    setClientToDelete(null);
  } catch (err) {
    console.error("Ошибка удаления клиента:", err);
  }
};



  /* ================= UI ================= */
  return (
    <div className="p-8 space-y-6 max-w-6xl mx-auto">

      {/* HEADER */}
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{t("clients")}</h1>

        <Button onClick={openCreateModal} variant="primary">
          {t("add_client")}
        </Button>
      </div>

      {/* LIST */}
      <div className="space-y-4">
        {clients.map((client) => (
          <Card
            key={client.id}
            hoverable
            className="cursor-pointer"
            onClick={() => navigate(`/clients/${client.id}`)}
          >
            <div className="flex items-start justify-between gap-6">

              {/* INFO */}
              <div className="space-y-2">
                <p>
                  <span className="font-semibold">{t("name")}:</span> {client.name}
                </p>

                <p>
                  <span className="font-semibold">{t("comment")}:</span>{" "}
                  {client.comment || "-"}
                </p>

                <p>
                  <span className="font-semibold">{t("tracking_code")}:</span>{" "}
                  {client.tracking_code || "-"}
                </p>
                <p>
                  <span className="font-semibold">{t("commission_rate")}:</span>{" "}
                  {client.commission_rate || "-"}
                </p>
              </div>

              {/* ACTIONS */}
              <div className="flex flex-wrap gap-3">

                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(client);
                  }}
                  variant="edit"
                >
                  {t("edit")}
                </Button>

                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setClientToDelete(client);
                    setShowDeleteConfirm(true);
                  }}
                  variant="danger"
                >
                  {t("delete")}
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* MODAL */}
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setEditingClient(null);
          setForm(emptyForm);
        }}
        title={editingClient ? t("edit_client") : t("new_client")}
      >
        <form onSubmit={handleSubmit} className="space-y-4">

          <input
            type="text"
            placeholder={t("name")}
            value={form.name}
            onChange={(e) =>
              setForm({ ...form, name: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
            required
          />
          
          <input
            type="number"
            step="0.01"
            placeholder={t("commission_rate")}
            value={form.commission_rate}
            onChange={(e) =>
              setForm({ ...form, commission_rate: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
          />

          <textarea
            placeholder={t("comment")}
            value={form.comment}
            onChange={(e) =>
              setForm({ ...form, comment: e.target.value })
            }
            className="w-full rounded-xl border px-4 py-3"
            rows={4}
          />

          <Button type="submit" variant="primary" className="w-full">
            {editingClient ? t("save") : t("create")}
          </Button>
        </form>
      </Modal>
      <ConfirmModal
  isOpen={showDeleteConfirm}
  onClose={() => {
    setShowDeleteConfirm(false);
    setClientToDelete(null);
  }}
  onConfirm={handleDeleteClient}
  title={t("delete_client")}
  description={
    <>
      {t("delete_confirm", { name: clientToDelete?.name })}?
    </>
  }
  confirmText={t("delete")}
/>
    </div>
  );
}